import { cn } from "@/lib/utils";

const STATUS = {
  NOUVEAU: "bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-700",
  MODIFIÉ: "bg-amber-50 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700",
  ANNULÉ: "bg-rose-50 text-rose-900 border-rose-300 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-700",
  CONFIRMÉ: "bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600",
  PROBABLE: "bg-blue-50 text-blue-800 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-700",
  "À VÉRIFIER": "bg-yellow-50 text-yellow-900 border-yellow-300 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-700",
  REPORTÉ: "bg-orange-50 text-orange-900 border-orange-300 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-700",
  COMPLET: "bg-purple-50 text-purple-900 border-purple-300 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-700",
  TERMINÉ: "bg-gray-100 text-gray-600 border-gray-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700",
  REJETÉ: "bg-gray-100 text-gray-500 border-gray-200 line-through",
  FUSIONNÉ: "bg-gray-100 text-gray-500 border-gray-200",
};
const DOT = { NOUVEAU: "bg-emerald-500", MODIFIÉ: "bg-amber-500", ANNULÉ: "bg-rose-600", REPORTÉ: "bg-orange-500", COMPLET: "bg-purple-500", PROBABLE: "bg-blue-500", "À VÉRIFIER": "bg-yellow-600", CONFIRMÉ: "bg-slate-500" };
export const STATUSES = Object.keys(STATUS).filter((s) => !["REJETÉ", "FUSIONNÉ"].includes(s));
export const STATUS_HEX = { NOUVEAU: "#10b981", MODIFIÉ: "#f59e0b", ANNULÉ: "#e11d48", REPORTÉ: "#f97316", COMPLET: "#a855f7", PROBABLE: "#3b82f6", "À VÉRIFIER": "#ca8a04", CONFIRMÉ: "#475569", TERMINÉ: "#9ca3af" };

const base = "inline-flex items-center gap-1 rounded-[3px] border px-1.5 py-[1px] text-[10px] font-mono font-semibold uppercase tracking-wide whitespace-nowrap";

export const StatusBadge = ({ status, testId }) => (
  <span data-testid={testId || "status-badge"} className={cn(base, STATUS[status] || STATUS.CONFIRMÉ)}>
    {DOT[status] && <span className={cn("h-1.5 w-1.5 rounded-full", DOT[status])} />}
    {status}
  </span>
);

export const WeakBadge = () => (
  <span data-testid="weak-signal-badge" className={cn(base, "bg-indigo-50 text-indigo-900 border-indigo-300 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-700")}>
    <span className="h-1.5 w-1.5 rounded-full bg-indigo-600 animate-pulse" />
    Signal faible
  </span>
);

export const ContradictionBadge = () => (
  <span data-testid="contradiction-badge" className={cn(base, "bg-rose-600 text-white border-rose-700")}>Contradiction</span>
);

export const confLevel = (c) => (c >= 80 ? "ÉLEVÉE" : c >= 50 ? "MOYENNE" : "À VÉRIFIER");

export const ConfBadge = ({ value, full = false }) => {
  const cls = value >= 80 ? "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
    : value >= 50 ? "bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800"
    : "bg-amber-50 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800";
  return (
    <span data-testid="confidence-badge" className={cn(base, cls)} title={`Confiance ${confLevel(value)}`}>
      {value ?? "—"}{full && ` · ${confLevel(value)}`}
    </span>
  );
};

export const InterestBar = ({ value }) => (
  <div className="flex items-center gap-1.5" data-testid="interest-score" title={`Intérêt territorial ${value}/100`}>
    <div className="h-1.5 w-10 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
      <div className={cn("h-full", value >= 70 ? "bg-indigo-600" : value >= 40 ? "bg-slate-500" : "bg-slate-300")} style={{ width: `${value || 0}%` }} />
    </div>
    <span className="font-mono text-[11px] tabular-nums">{value ?? "—"}</span>
  </div>
);

const PROV = {
  "SOURCÉE": "text-slate-600 border-slate-300", "RECOUPÉE": "text-emerald-700 border-emerald-300",
  "DÉDUITE": "text-amber-700 border-amber-300 border-dashed", MANUELLE: "text-blue-700 border-blue-300",
};
export const ProvTag = ({ prov }) => {
  if (!prov) return <span className="font-mono text-[9px] uppercase text-slate-400">inconnu</span>;
  const label = prov.type === "SOURCÉE" ? "Sourcée" : prov.type === "RECOUPÉE" ? `Recoupée ×${prov.confirmations?.length || 2}` : prov.type === "DÉDUITE" ? "Déduite" : "Corrigée manuellement";
  return <span className={cn("rounded-[3px] border px-1 font-mono text-[9px] uppercase", PROV[prov.type])} title={prov.source_name}>{label}</span>;
};
